import { useQuery } from "@tanstack/react-query";

import {
  fetchProjectById,
  fetchProjectSummary,
  fetchProjectOverview,
  fetchRequirementTypeById,
  fetchRequirementTypes,
  fetchWorkspacesByProjectId,
  fetchVersionsByWorkspaceId,
} from "./settings.api";

interface _QueryParams {
  versionId: number;
  testCaseFolderId?: number;
  page?: number;
  size?: number;
  query?: string;
  sort?: string;
}

type _GenericQueryParams = {
  query?: string;
  sort?: string;
  page?: number;
  size?: number;
  [key: string]: string | number | boolean | null | undefined;
};

export const useProjectById = (id: number) => {
  return useQuery({
    queryKey: ["projectById", id],
    queryFn: () => fetchProjectById(id),
    enabled: !!id, // avoids calling with undefined
  });
};
export const useRequirementTypes = (projectId: number, enabled?: boolean) =>
  useQuery({
    queryKey: ["requirementtypes", projectId],
    queryFn: () => fetchRequirementTypes(projectId),
    enabled,
  });

export const useRequirementTypeById = (priorityId: number) =>
  useQuery({
    queryKey: ["requirementtypebyid", priorityId],
    queryFn: () => fetchRequirementTypeById(priorityId),
    enabled: !!priorityId,
  });

export const useWorkspacesByProjectId = (projectId: number) =>
  useQuery({
    queryKey: ["workspacesByProject", projectId],
    queryFn: () => fetchWorkspacesByProjectId(projectId),
    enabled: !!projectId,
  });

export const useVersionsByWorkspaceId = (workspaceId: number) =>
  useQuery({
    queryKey: ["versionsByWorkspace", workspaceId],
    queryFn: () => fetchVersionsByWorkspaceId(workspaceId),
    enabled: !!workspaceId,
  });

/**
 * `GET /project/:id/summary` — aggregated per-workspace counts
 * (versionCount, suite/case/plan counts, lastRunAt). Powers the
 * `WorkspaceCardsPane` in the project Explorer.
 */
export const useProjectSummary = (
  projectId: number | null | undefined,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: ["projectSummary", projectId],
    queryFn: () => fetchProjectSummary(Number(projectId)),
    enabled: !!projectId && (options?.enabled ?? true),
    staleTime: 30 * 1000, // mildly fresh — counts change as users add tests
  });

/**
 * `GET /project/:id/overview` — rich aggregated dashboard metrics
 * (health, 12 KPIs, workspaces, members, activity, environments,
 * top failing module, missing-today). Powers the redesigned project
 * dashboard shell. See `settings.api.ts` `ProjectOverview` for the shape.
 */
export const useProjectOverview = (
  projectId: number | null | undefined,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: ["projectOverview", projectId],
    queryFn: () => fetchProjectOverview(Number(projectId)),
    enabled: !!projectId && (options?.enabled ?? true),
    staleTime: 30 * 1000,
  });
